import { useState } from 'react';
import { Loader2, Search, ListChecks } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { diagnosticsAPI, reviewAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Outcome -> badge colour. Anything not listed here (a new bucket added to
// services/invoice_row_explain.py) falls back to the neutral style rather
// than silently rendering unstyled -- see the default case below.
const OUTCOME_STYLES = {
  already_excluded_as_non_invoice: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  already_amount_parse_suspect: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  pending_exclusion_next_run: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  pending_suspect_flag_next_run: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  undispositioned_entity_resolved_gap: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  out_of_scope_review_status_approved_junk: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  out_of_scope_source: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  out_of_scope_review_status: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  out_of_scope_pending_review_resolved_entity: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

function fmtTimestamp(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return 'never';
  return then.toLocaleString('en-GB');
}

export function InvoiceRowExplainPanel() {
  const [invoiceId, setInvoiceId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [candidates, setCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [reversing, setReversing] = useState(false);

  const runExplain = async (idOverride) => {
    const id = (idOverride ?? invoiceId).trim();
    if (!id) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoiceRowExplain(id);
      setResult(data);
      setInvoiceId(id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceRowExplainCandidates();
      setCandidates(data?.candidates || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCandidatesLoading(false);
    }
  };

  const outcome = result?.sweep_eligibility?.outcome;
  const outcomeClass = OUTCOME_STYLES[outcome] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

  const reverseAutoReject = async () => {
    if (!result?.invoice?.id) return;
    setReversing(true);
    try {
      await reviewAPI.reverseAutoReject(result.invoice.id);
      toast.success('Reversed -- invoice restored to pending_review');
      await runExplain(result.invoice.id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReversing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Invoice Row Explain</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Pick an invoice id (or one of the current EUR0/missing-amount Gmail
          rows below) to see exactly why it renders the way it does on the Supplier
          Invoices list, and what the Non-Invoice Cleanup sweep has done -- or will do --
          with it. Re-runs the same classifier and predicates the sweep itself uses.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Input
            data-testid="invoice-row-explain-input"
            placeholder="Invoice id (gmail_18d2f...) or invoice number (INV-00358)"
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runExplain(); }}
            className="max-w-sm"
          />
          <Button data-testid="invoice-row-explain-run" onClick={() => runExplain()} disabled={loading || !invoiceId.trim()} size="sm">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Explain
          </Button>
        </div>

        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium flex items-center gap-1">
              <ListChecks className="h-3 w-3" />Current EUR0 / missing-amount Gmail rows
            </span>
            <Button data-testid="invoice-row-explain-load-candidates" onClick={loadCandidates} disabled={candidatesLoading} size="sm" variant="outline">
              {candidatesLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Load
            </Button>
          </div>
          {candidates && (
            <div className="border rounded overflow-x-auto max-h-72 overflow-y-auto" data-testid="invoice-row-explain-candidates-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 sticky top-0">
                    <th className="text-left px-2 py-1 font-medium">Date</th>
                    <th className="text-left px-2 py-1 font-medium">Sender</th>
                    <th className="text-left px-2 py-1 font-medium">Subject</th>
                    <th className="text-left px-2 py-1 font-medium">Review status</th>
                    <th className="text-left px-2 py-1 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-2 py-1 text-muted-foreground">{c.email_date || '-'}</td>
                      <td className="px-2 py-1">{c.email_from || '-'}</td>
                      <td className="px-2 py-1">{c.email_subject || '-'}</td>
                      <td className="px-2 py-1">{c.review_status || '-'}</td>
                      <td className="px-2 py-1">
                        <Button
                          data-testid={`invoice-row-explain-pick-${c.id}`}
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => runExplain(c.id)}
                        >
                          Explain
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {candidates.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-2 text-center text-muted-foreground">No matching rows.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {result?.disambiguation && (
          <div className="border rounded-lg p-3 space-y-2" data-testid="invoice-row-explain-disambiguation">
            <p className="text-xs text-muted-foreground">
              {result.candidates.length} invoices share the number "{result.lookup_value}" -- pick one.
            </p>
            <div className="border rounded overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 sticky top-0">
                    <th className="text-left px-2 py-1 font-medium">Invoice number</th>
                    <th className="text-left px-2 py-1 font-medium">Source</th>
                    <th className="text-left px-2 py-1 font-medium">Entity</th>
                    <th className="text-left px-2 py-1 font-medium">Amount</th>
                    <th className="text-left px-2 py-1 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {result.candidates.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-2 py-1 font-mono">{c.invoice_number || '-'}</td>
                      <td className="px-2 py-1">{c.source || '-'}</td>
                      <td className="px-2 py-1">{c.entity_id || '-'}</td>
                      <td className="px-2 py-1">{c.amount ?? '-'} {c.currency || ''}</td>
                      <td className="px-2 py-1">
                        <Button
                          data-testid={`invoice-row-explain-disambiguate-${c.id}`}
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => runExplain(c.id)}
                        >
                          Explain
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && !result.disambiguation && (
          <div className="border rounded-lg p-3 space-y-3 text-xs" data-testid="invoice-row-explain-result">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={outcomeClass} data-testid="invoice-row-explain-outcome">{outcome}</Badge>
              <span className="font-medium">{result.invoice?.email_subject || result.invoice?.id}</span>
            </div>
            <p className="text-muted-foreground">{result.sweep_eligibility?.explanation}</p>

            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <div><span className="text-muted-foreground">review_status:</span> {result.invoice?.review_status ?? '-'}</div>
              <div><span className="text-muted-foreground">entity_id:</span> {result.invoice?.entity_id ?? '-'}</div>
              <div><span className="text-muted-foreground">amount:</span> {result.invoice?.amount ?? 'null'}</div>
              <div><span className="text-muted-foreground">source:</span> {result.invoice?.source ?? '-'}</div>
              <div><span className="text-muted-foreground">excluded_as_non_invoice:</span> {String(!!result.invoice?.excluded_as_non_invoice)}</div>
              <div><span className="text-muted-foreground">amount_parse_suspect:</span> {String(!!result.invoice?.amount_parse_suspect)}</div>
              <div><span className="text-muted-foreground">has_pdf:</span> {String(!!result.classification?.has_pdf)}</div>
              <div><span className="text-muted-foreground">reject_reason:</span> {result.classification?.reject_reason ?? '(none -- not junk-shaped)'}</div>
              <div>
                <span className="text-muted-foreground">classified_document_type:</span>{' '}
                {result.invoice?.classified_document_type ?? '(not classified)'}
                {result.invoice?.classified_document_type_confidence != null &&
                  ` (${Math.round(result.invoice.classified_document_type_confidence * 100)}% confidence)`}
              </div>
              <div><span className="text-muted-foreground">classified_from:</span> {result.invoice?.classified_from ?? '-'}</div>
              <div><span className="text-muted-foreground">needs_type_review:</span> {String(!!result.invoice?.needs_type_review)}</div>
            </div>

            {result.invoice?.classified_document_type_unresolved_reason && (
              <div className="text-muted-foreground">
                Could not be classified: {result.invoice.classified_document_type_unresolved_reason}
                {' '}(the Gmail message could not be resolved by any tier -- Drive file, Gmail attachment, or body text).
              </div>
            )}

            {result.classification?.evidence && (
              <div><span className="text-muted-foreground">evidence:</span> {result.classification.evidence}</div>
            )}

            {result.invoice?.auto_rejected_by_classification && (
              <div className="border rounded-lg p-2 flex items-center justify-between gap-2 bg-red-50 dark:bg-red-900/10" data-testid="invoice-row-explain-auto-rejected">
                <span className="text-muted-foreground">
                  Auto-rejected as non-financial (classified_document_type={result.invoice.classified_document_type},
                  read from {result.invoice.classified_from || 'unknown source'}).
                  If this is mis-junked, reverse it back to pending_review.
                </span>
                <Button
                  data-testid="invoice-row-explain-reverse-auto-reject"
                  size="sm"
                  variant="outline"
                  disabled={reversing}
                  onClick={reverseAutoReject}
                >
                  {reversing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Reverse (mis-junked)
                </Button>
              </div>
            )}

            {result.invoice?.classification_reversed_at && (
              <div className="text-muted-foreground">
                Classification reject reversed at {fmtTimestamp(result.invoice.classification_reversed_at)} by{' '}
                {result.invoice.classification_reversed_by || 'unknown'}
              </div>
            )}

            {result.invoice?.quarantine_field && (
              <div className="text-muted-foreground">
                Quarantined on field "{result.invoice.quarantine_field}" (raw value {result.invoice.quarantine_raw_value}),
                source={result.invoice.quarantine_source}, at {fmtTimestamp(result.invoice.quarantined_at)}
              </div>
            )}

            {result.last_apply_chain && (
              <div className="text-muted-foreground" data-testid="invoice-row-explain-last-chain">
                Last Apply chain: {result.last_apply_chain.status}, run {result.last_apply_chain.runs_count},
                last active {fmtTimestamp(result.last_apply_chain.heartbeat_at)} --{' '}
                {result.last_apply_chain.considered_this_pass ? 'this doc was within the walked range' : 'this doc has not been reached yet this pass'}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
